import React, { useState } from 'react';
import { CoreEntraIdUserExportPrimaryEmailAddressSettings, EntraIdUserExportPrimaryEmailAddressSource, EntraIdUserExportSettingBehaviour } from '../../../../../../models/CoreEntraIdUserModels';
import EnumInput from '../../../../../misc/EnumInput';
import * as EntraId from './EntraIdUserTypes';
import BoolInput from '../../../../../misc/BoolInput';
import TextInput from '../../../../../misc/TextInput';
import StringValueBuilderInput from '../StringValueBuilderInput';
import { Button, Card, CardBody, CardHeader, Collapse } from 'reactstrap';
import { useTranslation } from 'react-i18next';


interface EntraIdUserEmailAddressSettingsProps {
    primaryEmailAddressSettings: CoreEntraIdUserExportPrimaryEmailAddressSettings
    disabled?: boolean
    validation: string[]
    onChange: (x: CoreEntraIdUserExportPrimaryEmailAddressSettings) => void
}

const EntraIdUserEmailAddressSettings: React.FC<EntraIdUserEmailAddressSettingsProps> = (props) => {
    const [isOpen, setOpen] = useState<boolean>(true);
    const { t } = useTranslation(['configuration', 'common'])

    const updateConfig = (change: Partial<CoreEntraIdUserExportPrimaryEmailAddressSettings>) => {
        props.onChange({ ...props.primaryEmailAddressSettings, ...change })
    }

    return (<Card className="mb-3">
        <CardHeader tag="h5">
            <Button outline color="secondary" style={{ width: '30px', float: 'right', lineHeight: '0.8', padding: '0.5rem' }} onClick={() => setOpen(!isOpen)}>{isOpen ? '-' : '+'}</Button>
            {t('entraIdUserEmailAddressSettings.header', { ns: 'configuration' })}
        </CardHeader>
        <Collapse isOpen={isOpen}>
            <CardBody>
                <EnumInput
                    title={t('entraIdUserEmailAddressSettings.source.title', { ns: 'configuration' })}
                    name="primaryEmailAddressSettings.source"
                    value={props.primaryEmailAddressSettings.source}
                    options={EntraId.ExportPrimaryEmailAddressSource.map(key => ({
                        value: key,
                        text: t(`entraIdUserEmailAddressSettings.source.options.${key}`, { ns: 'configuration' })
                    }))}
                    onChange={(_, value) => updateConfig({ source: value })}
                    disabled={props.disabled}
                />

                {(props.primaryEmailAddressSettings.source === EntraIdUserExportPrimaryEmailAddressSource.GENERATED) &&
                    <StringValueBuilderInput
                        name='primaryEmailAddressSettings.stringValueBuilderParts'
                        parts={props.primaryEmailAddressSettings.stringValueBuilderParts}
                        onChange={(_, value) => updateConfig({ stringValueBuilderParts: value })}
                        disabled={props.disabled}
                    />}

                <BoolInput
                    title={t('entraIdUserEmailAddressSettings.forceDomainName.title', { ns: 'configuration' })}
                    name="primaryEmailAddressSettings.forceDomainName"
                    value={props.primaryEmailAddressSettings.forceDomainName}
                    disabled={props.disabled}
                    updateValue={(_, value) => updateConfig({ forceDomainName: value })}
                    invalidFeedback={t('entraIdUserEmailAddressSettings.forceDomainName.invalidFeedback', { ns: 'configuration' })}
                    invalid={props.validation.indexOf("primaryEmailAddressSettings.forceDomainName") !== -1}
                />

                {(props.primaryEmailAddressSettings.forceDomainName) &&
                    <TextInput
                        title={t('entraIdUserEmailAddressSettings.forceDomainNameValue.title', { ns: 'configuration' })}
                        name="primaryEmailAddressSettings.forceDomainNameValue"
                        value={props.primaryEmailAddressSettings.forceDomainNameValue || ""}
                        disabled={props.disabled}
                        updateValue={(_, value) => updateConfig({ forceDomainNameValue: value })}
                        invalidFeedback={t('entraIdUserEmailAddressSettings.forceDomainNameValue.invalidFeedback', { ns: 'configuration' })}
                        invalid={props.validation.indexOf("primaryEmailAddressSettings.forceDomainNameValue") !== -1}
                    />}

                <EnumInput
                    title={t('entraIdUserEmailAddressSettings.settingBehaviour.title', { ns: 'configuration' })}
                    name="primaryEmailAddressSettings.settingBehaviour"
                    value={props.primaryEmailAddressSettings.settingBehaviour}
                    options={EntraId.ExportSettingsBehaviour.map(key => ({
                        value: key,
                        text: t(`entraIdUserEmailAddressSettings.settingBehaviour.options.${key}`, { ns: 'configuration' })
                    }))}
                    onChange={(_, value) => updateConfig({ settingBehaviour: value })}
                    disabled={props.disabled}
                />

                {(props.primaryEmailAddressSettings.settingBehaviour === EntraIdUserExportSettingBehaviour.FORCE) &&
                    <EnumInput
                        title={t('entraIdUserEmailAddressSettings.aliasHandling.title', { ns: 'configuration' })}
                        name="primaryEmailAddressSettings.aliasHandling"
                        value={props.primaryEmailAddressSettings.aliasHandling}
                        options={EntraId.ExportAliasHandling.map(key => ({
                            value: key,
                            text: t(`entraIdUserEmailAddressSettings.aliasHandling.options.${key}`, { ns: 'configuration' })
                        }))}
                        onChange={(_, value) => updateConfig({ aliasHandling: value })}
                        disabled={props.disabled}
                    />}
            </CardBody>
        </Collapse>
    </Card>);

}

export default EntraIdUserEmailAddressSettings;
